"use strict";
/**
 * QRCodeService - Generates QR codes for results sharing
 * Encodes calculator data and results into compact base64 format for efficient QR codes
 *
 * Data format: [country, lang, fleetSize, distance, efficiency, workDays, idleTime, fuelCost, idle, optimization, driverBehavior, total, carbon]
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.QRCodeService = void 0;
class QRCodeService {
    /**
     * Generate a QR code for results with encoded URL
     * Displays QR code in the specified container
     */
    static generateResultsQRCode(calculatorData, results, country, lang) {
        try {
            // Clear any previous QR code
            const container = document.getElementById('fcc-qrcode');
            if (!container) {
                console.warn('QR code container not found');
                return;
            }
            container.innerHTML = '';
            // Encode data to URL
            const url = this.encodeResultsToURL(calculatorData, results, country, lang);
            // Generate QR code using qrcodejs library
            if (typeof window.QRCode !== 'undefined') {
                new window.QRCode(container, {
                    text: url,
                    width: 200,
                    height: 200,
                    correctLevel: window.QRCode.CorrectLevel.L,
                });
            }
            else {
                console.error('QRCode library not loaded');
            }
        }
        catch (error) {
            console.error('Error generating QR code:', error);
        }
    }
    /**
     * Encode results data into compact base64 format for efficient QR codes
     * Creates array: [country, lang, fleetSize, distance, efficiency, workDays, idleTime, fuelCost, idle, optimization, driverBehavior, total, carbon]
     * Encoded as base64 to minimize QR code size
     */
    static encodeResultsToURL(calculatorData, results, country, lang) {
        // Get the base URL without query parameters
        const pathname = window.location.pathname;
        const baseUrl = window.location.origin + pathname;
        // Create compact array with all data
        // Using short property names and array format to minimize JSON size
        const dataArray = [
            country, // 0: country code
            lang, // 1: language code
            calculatorData.fleetSize, // 2: fleet size
            calculatorData.estimatedDistance, // 3: distance
            calculatorData.fuelEfficiency, // 4: fuel efficiency
            calculatorData.workDays, // 5: work days
            calculatorData.idleTime, // 6: idle time
            calculatorData.fuelCost, // 7: fuel cost
            results.idle, // 8: idle savings
            results.optimization, // 9: optimization savings
            results.driverBehavior, // 10: driver behavior savings
            results.total, // 11: total savings
            results.carbon, // 12: carbon reduction
        ];
        // Convert array to JSON and then to base64
        const jsonString = JSON.stringify(dataArray);
        const base64Data = btoa(jsonString);
        return `${baseUrl}?r=${encodeURIComponent(base64Data)}`;
    }
    /**
     * Decode results data from base64-encoded URL parameter
     * Parses compact array format: [country, lang, fleetSize, distance, efficiency, workDays, idleTime, fuelCost, idle, optimization, driverBehavior, total, carbon]
     * Returns null if not in results mode or if data is invalid
     */
    static decodeResultsFromURL() {
        const params = new URLSearchParams(window.location.search);
        // Check if this is a results URL (new format with 'r' parameter)
        const encodedData = params.get('r');
        if (!encodedData) {
            return null;
        }
        try {
            // Decode base64 to JSON string
            const jsonString = atob(decodeURIComponent(encodedData));
            const dataArray = JSON.parse(jsonString);
            // Validate array length
            if (!Array.isArray(dataArray) || dataArray.length < 13) {
                console.error('Invalid results data format');
                return null;
            }
            // Extract values from array
            const [country, lang, fleetSize, estimatedDistance, fuelEfficiency, workDays, idleTime, fuelCost, idle, optimization, driverBehavior, total, carbon,] = dataArray;
            return {
                country: country || 'GB',
                lang: lang || 'en',
                calculatorData: {
                    country: country || 'GB',
                    fleetSize: Number(fleetSize) || 0,
                    estimatedDistance: Number(estimatedDistance) || 0,
                    fuelEfficiency: Number(fuelEfficiency) || 0,
                    workDays: Number(workDays) || 0,
                    idleTime: Number(idleTime) || 0,
                    fuelCost: Number(fuelCost) || 0,
                },
                results: {
                    idle: Number(idle) || 0,
                    optimization: Number(optimization) || 0,
                    driverBehavior: Number(driverBehavior) || 0,
                    total: Number(total) || 0,
                    carbon: Number(carbon) || 0,
                },
            };
        }
        catch (error) {
            console.error('Error decoding results from URL:', error);
            return null;
        }
    }
}
exports.QRCodeService = QRCodeService;
